import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import { getAuthHeaders } from '../../auth';
import TableScrollContainer from '../common/TableScrollContainer';
import Select2Dropdown from '../common/Select2Dropdown';
import { openFilePreview } from '../common/FilePreviewModal';
import { usePermissions } from '../../services/PermissionsContext';
import useSortableTable from '../common/useSortableTable';
import JobForm from '../documentation/JobForm';

const API_URL = '/api/documentation/jobs/permit-report/';
const JOBS_API_URL = '/api/documentation/jobs/';
const ADMIN_API_URL = '/api/admin/';
const MASTERS_API_URL = '/api/masters/';


const CONDITIONAL_RESULT_OPTIONS = [
    { value: 'PASSED', label: 'Passed' },
    { value: 'FAILED', label: 'Failed' },
    { value: 'GUIDANCE_REJECTED', label: 'Guidance Rejected' },
];

// Permit types eligible for the conditional-release workflow
const isConditionalPermitType = (permitType) => {
    const t = (permitType || '').toUpperCase();
    return t.includes('CONDITIONAL') || t.includes('TBS DI') || t.includes('PVOC');
};

const getReleaseStatusOptions = (permitType) => {
    if ((permitType || '').toUpperCase().includes('PVOC')) {
        return [
            { value: 'CONDITIONAL', label: 'Conditional' },
            { value: 'MONITORING', label: 'Monitoring' },
        ];
    }
    // TBS DI and other "Conditional" permit types
    return [
        { value: 'CONDITIONAL', label: 'Conditional' },
        { value: 'NORMAL', label: 'Normal' },
    ];
};

const ConditionalPermits = ({ searchQuery = '' }) => {
    const { hasPermission } = usePermissions();
    const canEdit = hasPermission('PERMIT REPORTS', 'can_edit');
    const canDeletePermits = hasPermission('PERMIT REPORTS', 'can_delete');
    const [reportData, setReportData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('sample-client');

    const [jobFormModal, setJobFormModal] = useState({ isOpen: false, job: null });

    // Dialog state
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentPermit, setCurrentPermit] = useState(null); // { jobId, permitType, blNumber, lpcoId }
    const [authNumber, setAuthNumber] = useState('');
    const [remarks, setRemarks] = useState('');
    const [releaseStatus, setReleaseStatus] = useState('');
    const [sampleCollected, setSampleCollected] = useState(null); // null | true | false
    const [sampleCollectedDate, setSampleCollectedDate] = useState('');
    const [sampleLocation, setSampleLocation] = useState(''); // 'PORT' | 'CLIENT_PREMISES'
    const [openedFromClientTab, setOpenedFromClientTab] = useState(false);
    const [openedFromFailedTab, setOpenedFromFailedTab] = useState(false);
    const [openedFromGuidanceRejectedTab, setOpenedFromGuidanceRejectedTab] = useState(false);
    const [openedFromPassedTab, setOpenedFromPassedTab] = useState(false);
    const [guidanceLetterResult, setGuidanceLetterResult] = useState('');
    const [finalStatus, setFinalStatus] = useState('');
    const [failedRemarks, setFailedRemarks] = useState('');
    const [historyModal, setHistoryModal] = useState({ open: false, loading: false, rows: [] });
    // Saved values open read-only; delete-permission users unlock them via Edit
    const [isEditingUnlocked, setIsEditingUnlocked] = useState(false);
    const [conditionalResult, setConditionalResult] = useState('');
    const [saving, setSaving] = useState(false);

    const [documentsToUpload, setDocumentsToUpload] = useState([]);
    const [permitMappings, setPermitMappings] = useState([]);
    const [docsCollapsed, setDocsCollapsed] = useState(false);
    // Additional documents added from the Guidance Rejected tab — each row
    // queues { key, document_type, file_to_upload, remarks, previewUrl }
    const [jobDocuments, setJobDocuments] = useState([]);
    const [extraDocs, setExtraDocs] = useState([]);
    const [permitDocTypes, setPermitDocTypes] = useState([]);
    const extraDocKeyRef = useRef(0);


    const fetchReportData = useCallback(async (silent = false, search = '') => {
        if (!silent) setLoading(true);
        try {
            const res = await fetch(search ? `${API_URL}?search=${encodeURIComponent(search)}` : API_URL, { headers: getAuthHeaders() });
            if (!res.ok) {
                throw new Error('Failed to fetch permit report data');
            }
            const data = await res.json();
            setReportData(data);
        } catch (error) {
            toast.error(error.message);
        } finally {
            if (!silent) setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchReportData(false, searchQuery.trim()); // honor a search already active in PermitReport
        fetchPermitMappings();
        fetchPermitDocTypes();
    }, [fetchReportData]);

    // Debounced server search — lets the shared search box reach permits approved
    // beyond the default 2-month window. Clearing restores the limited dataset.
    const isInitialSearch = useRef(true);
    useEffect(() => {
        if (isInitialSearch.current) { isInitialSearch.current = false; return; }
        const t = setTimeout(() => fetchReportData(true, searchQuery.trim()), 400);
        return () => clearTimeout(t);
    }, [searchQuery, fetchReportData]);

    const fetchPermitMappings = async () => {
        try {
            const res = await fetch(`${ADMIN_API_URL}permit-document-mappings/`, { headers: getAuthHeaders() });
            if (res.ok) {
                const data = await res.json();
                setPermitMappings(data);
            }
        } catch (error) {
            toast.error("Failed to load permit-document mappings.");
        }
    };

    // Document types configured for the Permits department in List
    // Configuration — source for the Add Document dropdown (Guidance Rejected)
    const fetchPermitDocTypes = async () => {
        try {
            const res = await fetch(`${MASTERS_API_URL}document-types/?department_name=${encodeURIComponent('Permits')}`, { headers: getAuthHeaders() });
            if (res.ok) {
                const data = await res.json();
                setPermitDocTypes(Array.isArray(data) ? data : (data.results || []));
            }
        } catch (error) {
            toast.error('Failed to load permit document types.');
        }
    };

    // Document slots visible for the current modal state — workflow docs only
    // appear once the selection that requires them is made.
    const WORKFLOW_DOC_TYPES = ['TEST RESULT', 'GUIDANCE LETTER', 'SAMPLE COLLECTION FORM', 'INSPECTION BATCH CERTIFICATE'];
    const visibleDocsToUpload = documentsToUpload.filter(doc => {
        const dt = (doc.document_type || '').toUpperCase();
        if (dt === 'TEST RESULT') return !!conditionalResult;
        if (dt === 'GUIDANCE LETTER') return openedFromFailedTab || guidanceLetterResult === 'REJECTED';
        if (dt === 'SAMPLE COLLECTION FORM') return sampleLocation === 'CLIENT_PREMISES' || openedFromClientTab;
        if (dt === 'INSPECTION BATCH CERTIFICATE') return (currentPermit?.permitType || '').toUpperCase().includes('PVOC') && sampleCollected === true && !!releaseStatus;
        return true;
    });
    const pendingRequiredDocs = visibleDocsToUpload.some(doc =>
        WORKFLOW_DOC_TYPES.includes((doc.document_type || '').toUpperCase()) &&
        !doc.existing_document && !doc.file_to_upload);

    // A required-but-missing document forces the Documents section open
    const docsExpanded = pendingRequiredDocs || !docsCollapsed;
    useEffect(() => {
        if (pendingRequiredDocs) setDocsCollapsed(false);
    }, [pendingRequiredDocs]);

    const openJobFormModal = async (jobId) => {
        try {
            const res = await fetch(`${JOBS_API_URL}${jobId}/`, { headers: getAuthHeaders() });
            if (!res.ok) { toast.error('Failed to load job details'); return; }
            const data = await res.json();
            setJobFormModal({ isOpen: true, job: data });
        } catch (e) {
            toast.error('Error loading job');
        }
    };

    const closeJobFormModal = () => {
        setJobFormModal({ isOpen: false, job: null });
    };

    const handleOpenModal = async (jobId, permitType, blNumber, permit) => {
        const mapping = permitMappings.find(m => m.permit_type === permitType);
        let requiredDocTypes = mapping ? mapping.document_types.map(dt => dt.name) : [];

        if (permitType === 'GCLA' && !requiredDocTypes.some(dt => dt.toUpperCase() === 'GCLA TRANSPORT PERMIT')) {
            requiredDocTypes.push('GCLA TRANSPORT PERMIT');
        }

        // Conditional workflow needs Test Result / Guidance Letter slots, plus
        // the Sample Collection Form (client-premises samples) and the
        // Inspection Batch Certificate (PVOC results).
        const workflowDocTypes = ['TEST RESULT', 'GUIDANCE LETTER', 'SAMPLE COLLECTION FORM'];
        if ((permitType || '').toUpperCase().includes('PVOC')) {
            workflowDocTypes.push('INSPECTION BATCH CERTIFICATE');
        }
        workflowDocTypes.forEach(dt => {
            if (!requiredDocTypes.some(d => d.toUpperCase() === dt)) {
                requiredDocTypes.push(dt);
            }
        });

        setDocumentsToUpload(requiredDocTypes.map(docType => ({
            document_type: docType,
            existing_document: null,
            file_to_upload: null,
            previewUrl: null,
        })));
        setCurrentPermit({
            jobId, permitType, blNumber, lpcoId: permit.id,
            saved: {
                sampleCollected: permit.sample_collected === true,
                releaseStatus: !!permit.release_status,
                sampleLocation: !!permit.sample_location,
                sampleCollectedDate: !!permit.sample_collected_date,
                conditionalResult: !!permit.conditional_result,
                guidanceLetterReleased: !!permit.guidance_letter_released,
                finalStatus: !!permit.final_status,
                remarks: !!permit.remarks,
                failedRemarks: !!permit.failed_remarks,
            },
        });
        setIsEditingUnlocked(false);
        setAuthNumber(permit.application_no || '');
        setRemarks(permit.remarks || '');
        setReleaseStatus(permit.release_status || '');
        setSampleCollected(permit.sample_collected === true ? true : null);
        setSampleCollectedDate(permit.sample_collected_date || '');
        setSampleLocation(permit.sample_location || '');
        setOpenedFromClientTab(activeTab === 'sample-client');
        // Guidance Rejected records are failed samples whose letter was rejected —
        // the modal shows the same failed-workflow controls for them
        setOpenedFromFailedTab(activeTab === 'failed' || activeTab === 'guidance-rejected');
        setOpenedFromGuidanceRejectedTab(activeTab === 'guidance-rejected');
        setOpenedFromPassedTab(activeTab === 'passed');
        setGuidanceLetterResult(permit.guidance_letter_released || '');
        setFinalStatus(permit.final_status || '');
        setFailedRemarks(permit.failed_remarks || '');
        setConditionalResult(permit.conditional_result || '');
        setExtraDocs([]);
        setJobDocuments([]);
        setIsModalOpen(true);

        try {
            const res = await fetch(`${JOBS_API_URL}${jobId}/documents/`, { headers: getAuthHeaders() });
            if (res.ok) {
                const jobDocs = await res.json();
                setJobDocuments(jobDocs);
                // Workflow documents are scoped per permit — prefer a document
                // tagged to this LPCO, then fall back to a legacy untagged one
                const findJobDoc = (predicate) =>
                    jobDocs.find(d => d.lpco === permit.id && predicate(d)) ||
                    jobDocs.find(d => d.lpco == null && predicate(d));
                setDocumentsToUpload(requiredDocTypes.map(docType => {
                    let existingDoc = findJobDoc(d => d.document_type === docType);
                    if (!existingDoc) {
                        existingDoc = findJobDoc(d => (d.document_type || '').toUpperCase() === docType.toUpperCase());
                    }
                    if (!existingDoc && docType.toUpperCase() === 'GCLA TRANSPORT PERMIT') {
                        existingDoc = findJobDoc(d => d.document_type === 'TRANSPORT PERMIT');
                    }
                    return {
                        document_type: docType,
                        existing_document: existingDoc || null,
                        file_to_upload: null,
                        previewUrl: null,
                    };
                }));
            }
        } catch (e) {
            toast.error('Could not load existing documents.');
        }
    };

    const handleCloseModal = () => {
        documentsToUpload.forEach(doc => {
            if (doc.previewUrl) {
                URL.revokeObjectURL(doc.previewUrl);
            }
        });
        setIsModalOpen(false);
        setCurrentPermit(null);
        setDocumentsToUpload([]);
        extraDocs.forEach(doc => {
            if (doc.previewUrl) URL.revokeObjectURL(doc.previewUrl);
        });
        setExtraDocs([]);
        setJobDocuments([]);
        setReleaseStatus('');
        setSampleCollected(null);
        setSampleCollectedDate('');
        setSampleLocation('');
        setOpenedFromClientTab(false);
        setOpenedFromFailedTab(false);
        setOpenedFromGuidanceRejectedTab(false);
        setOpenedFromPassedTab(false);
        setGuidanceLetterResult('');
        setFinalStatus('');
        setFailedRemarks('');
        setConditionalResult('');
        setIsEditingUnlocked(false);
    };

    const openHistoryModal = async () => {
        if (!currentPermit?.jobId || !currentPermit?.lpcoId) return;
        setHistoryModal({ open: true, loading: true, rows: [] });
        try {
            const res = await fetch(`${JOBS_API_URL}${currentPermit.jobId}/lpcos/${currentPermit.lpcoId}/history/`, { headers: getAuthHeaders() });
            if (!res.ok) throw new Error('Failed to load permit history.');
            const data = await res.json();
            // Merge field changes written by the same user in the same save into
            // one row — each changed field on its own line; unchanged values
            // skipped. Grouped by timestamp; only the date is displayed.
            const grouped = [];
            data.forEach(row => {
                if (row.previous === row.new) return;
                const dt = row.date ? new Date(row.date) : null;
                const minuteKey = dt ? dt.getTime() - dt.getSeconds() * 1000 - dt.getMilliseconds() : 0;
                const label = row.previous
                    ? `${row.field}: ${row.previous} → ${row.new || '—'}`
                    : `${row.field}: ${row.new || '—'}`;
                const last = grouped[grouped.length - 1];
                if (last && last.minuteKey === minuteKey && last.user === row.user) {
                    last.changes.push(label);
                } else {
                    grouped.push({ minuteKey, date: row.date, user: row.user, changes: [label] });
                }
            });
            setHistoryModal({ open: true, loading: false, rows: grouped });
        } catch (e) {
            toast.error(e.message || 'Failed to load permit history.');
            setHistoryModal({ open: false, loading: false, rows: [] });
        }
    };

    const handleFileChange = (docType, file) => {
        const previewUrl = file ? URL.createObjectURL(file) : null;
        setDocumentsToUpload(prev => prev.map(doc => {
            if (doc.document_type === docType && doc.previewUrl) {
                URL.revokeObjectURL(doc.previewUrl);
            }
            return doc.document_type === docType ? { ...doc, file_to_upload: file, previewUrl: previewUrl } : doc;
        }));
    };

    const cancelFileSelection = (docType) => {
        setDocumentsToUpload(prev => prev.map(doc => {
            if (doc.document_type === docType) {
                if (doc.previewUrl) {
                    URL.revokeObjectURL(doc.previewUrl);
                }
                return { ...doc, file_to_upload: null, previewUrl: null };
            }
            return doc;
        }));
    };

    // Additional documents queued from the Guidance Rejected tab
    const addExtraDoc = () => {
        extraDocKeyRef.current += 1;
        setExtraDocs(prev => [...prev, { key: extraDocKeyRef.current, document_type: '', file_to_upload: null, remarks: '', previewUrl: null }]);
    };
    const updateExtraDoc = (key, values) =>
        setExtraDocs(prev => prev.map(d => (d.key === key ? { ...d, ...values } : d)));
    const setExtraDocFile = (key, file) =>
        setExtraDocs(prev => prev.map(d => {
            if (d.key !== key) return d;
            if (d.previewUrl) URL.revokeObjectURL(d.previewUrl);
            return { ...d, file_to_upload: file, previewUrl: file ? URL.createObjectURL(file) : null };
        }));
    const removeExtraDoc = (key) =>
        setExtraDocs(prev => {
            const target = prev.find(d => d.key === key);
            if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);
            return prev.filter(d => d.key !== key);
        });

    const handleSavePermit = async () => {
        if (!canEdit && !canDeletePermits) {
            toast.error("You do not have permission to perform this action.");
            return;
        }
        if (!currentPermit?.lpcoId) {
            toast.error('No permit record selected.');
            return;
        }

        // Every queued additional document needs a type, a file and remarks
        for (const extra of extraDocs) {
            if (!extra.document_type) {
                toast.error('Please select a Document Type for each additional document.');
                return;
            }
            if (!extra.file_to_upload) {
                toast.error(`Please attach a file for ${extra.document_type}.`);
                return;
            }
            if (!extra.remarks.trim()) {
                toast.error(`Remarks are required for ${extra.document_type}.`);
                return;
            }
        }

        const validDocuments = documentsToUpload.filter(d => d.file_to_upload);

        if (validDocuments.length === 0 && extraDocs.length === 0 && !authNumber && !remarks && !releaseStatus && !conditionalResult && sampleCollected === null && !sampleLocation) {
            toast.info('Nothing to save. Please make a selection, upload a document, or add remarks.');
            return;
        }

        // Release Status radios are PVOC-only; other conditional types keep their
        // saved status (defaulting to CONDITIONAL when unset).
        const isPvoc = (currentPermit?.permitType || '').toUpperCase().includes('PVOC');
        const effectiveReleaseStatus = isPvoc ? releaseStatus : (releaseStatus || 'CONDITIONAL');
        if (sampleCollected === null) {
            toast.error('Please select whether a sample has been collected (Yes or No).');
            return;
        }
        if (isPvoc && sampleCollected === true && !releaseStatus) {
            toast.error('Please select a Release Status (Conditional or Monitoring).');
            return;
        }
        if (isPvoc && sampleCollected === true && releaseStatus) {
            const batchDoc = documentsToUpload.find(d => (d.document_type || '').toUpperCase() === 'INSPECTION BATCH CERTIFICATE');
            if (!batchDoc || (!batchDoc.file_to_upload && !batchDoc.existing_document)) {
                toast.error('Please upload the Inspection Batch Certificate for the selected release status.');
                return;
            }
        }
        if (sampleCollected === true) {
            if (!sampleLocation) {
                toast.error('Please select where the sample was collected (Port or Client Premises).');
                return;
            }
            if ((sampleLocation === 'PORT' || openedFromClientTab) && !sampleCollectedDate) {
                toast.error('Please enter the date the sample was collected.');
                return;
            }
            if (sampleLocation === 'CLIENT_PREMISES' && sampleCollectedDate) {
                const scfDoc = documentsToUpload.find(d => (d.document_type || '').toUpperCase() === 'SAMPLE COLLECTION FORM');
                if (!scfDoc || (!scfDoc.file_to_upload && !scfDoc.existing_document)) {
                    toast.error('Please attach the Sample Collection Form.');
                    return;
                }
            }
        }
        if (sampleCollected === true && sampleCollectedDate && new Date(sampleCollectedDate) > new Date()) {
            toast.error('Date collected cannot be a future date.');
            return;
        }
        if ((effectiveReleaseStatus || sampleCollected === true || conditionalResult) && !remarks.trim()) {
            toast.error('Remarks are required.');
            return;
        }
        // From the Failed tab, Guidance Letter Released (Released/Rejected) is
        // saved as its own field — Released moves the record to Passed,
        // Rejected to Guidance Rejected. The test result stays Failed.
        const effectiveResult = conditionalResult;
        const guidanceDocSlot = documentsToUpload.find(d => (d.document_type || '').toUpperCase() === 'GUIDANCE LETTER');
        const guidanceDocAttached = !!(guidanceDocSlot && (guidanceDocSlot.file_to_upload || guidanceDocSlot.existing_document));
        if (openedFromFailedTab && guidanceLetterResult && !guidanceDocAttached) {
            toast.error('Please attach the Guidance Letter before releasing or rejecting it.');
            return;
        }
        // Once a Guidance Letter is attached the release/reject decision is
        // mandatory — the record can't sit in Failed/Guidance Rejected undecided.
        if (openedFromFailedTab && effectiveResult === 'FAILED' && guidanceDocAttached && !guidanceLetterResult) {
            toast.error('Please select whether the Guidance Letter is Released or Rejected.');
            return;
        }
        if (effectiveResult) {
            if (sampleCollected !== true) {
                toast.error('A sample must be collected before recording a test result.');
                return;
            }
            const findDocSlot = (docType) => documentsToUpload.find(d => (d.document_type || '').toUpperCase() === docType);
            const testResultDoc = findDocSlot('TEST RESULT');
            if (!testResultDoc || (!testResultDoc.file_to_upload && !testResultDoc.existing_document)) {
                toast.error('Please upload the Test Result document.');
                return;
            }
            if (isPvoc && !releaseStatus) {
                const batchDoc = findDocSlot('INSPECTION BATCH CERTIFICATE');
                if (!batchDoc || (!batchDoc.file_to_upload && !batchDoc.existing_document)) {
                    toast.error('Please upload the Inspection Batch Certificate along with the Test Result.');
                    return;
                }
            }
            if (effectiveResult === 'FAILED' && !failedRemarks.trim()) {
                toast.error('Failed Remarks are required when the test result is Failed.');
                return;
            }
            if (effectiveResult === 'FAILED') {
                const guidanceDoc = findDocSlot('GUIDANCE LETTER');
                if (!guidanceDoc || (!guidanceDoc.file_to_upload && !guidanceDoc.existing_document)) {
                    // Warn but allow saving — the Failed tab/row flags it for follow-up
                    toast.warning('Please Attach Guidance Letter when available.');
                }
            }
        }

        setSaving(true);

        try {
            // --- Step 1: Upload any newly selected documents ---
            if (validDocuments.length > 0 || extraDocs.length > 0) {
                const docPayload = new FormData();
                let docIndex = 0;
                validDocuments.forEach((doc) => {
                    docPayload.append(`file_${docIndex}`, doc.file_to_upload);
                    docPayload.append(`doc_${docIndex}`, JSON.stringify({
                        document_type: doc.document_type,
                        remarks: `Permit Application: ${authNumber || 'Document Only'}`,
                        lpco_id: currentPermit.lpcoId,
                    }));
                    docIndex += 1;
                });
                // Additional documents keep their own mandatory remarks and are
                // attributed to this permit via lpco_id
                extraDocs.forEach((doc) => {
                    docPayload.append(`file_${docIndex}`, doc.file_to_upload);
                    docPayload.append(`doc_${docIndex}`, JSON.stringify({
                        document_type: doc.document_type,
                        remarks: doc.remarks.trim(),
                        lpco_id: currentPermit.lpcoId,
                    }));
                    docIndex += 1;
                });

                const docRes = await fetch(`${JOBS_API_URL}${currentPermit.jobId}/save-documents/`, {
                    method: 'POST',
                    headers: getAuthHeaders(true),
                    body: docPayload,
                });

                if (!docRes.ok) {
                    throw new Error('Failed to upload permit document(s).');
                }
                toast.success(`${validDocuments.length + extraDocs.length} document(s) uploaded successfully!`);
            }

            // --- Step 2: Save permit details ---
            const patchPayload = {
                application_no: authNumber,
                remarks,
                release_status: effectiveReleaseStatus,
                sample_collected: sampleCollected === true,
                sample_collected_date: sampleCollected === true ? (sampleCollectedDate || null) : null,
                sample_location: sampleCollected === true ? sampleLocation : '',
                conditional_result: effectiveResult || '',
                guidance_letter_released: guidanceLetterResult || '',
                final_status: finalStatus || '',
                // Failed remarks are kept for audit history — only updated while
                // the result is Failed, never cleared when the record moves on
                ...(effectiveResult === 'FAILED' ? { failed_remarks: failedRemarks.trim() } : {}),
            };
            const patchRes = await fetch(`${JOBS_API_URL}${currentPermit.jobId}/lpcos/${currentPermit.lpcoId}/`, {
                method: 'PATCH',
                headers: getAuthHeaders(),
                body: JSON.stringify(patchPayload)
            });
            if (!patchRes.ok) {
                const errorData = await patchRes.json().catch(() => ({}));
                throw new Error(errorData.detail || 'Failed to update permit details.');
            }
            toast.success('Permit details updated successfully!');

            // Apply the saved values to the record in place so it moves between
            // tabs immediately — the silent refresh reconciles the full dataset.
            const docAttached = (docType) => documentsToUpload.some(d =>
                (d.document_type || '').toUpperCase() === docType && (d.file_to_upload || d.existing_document));
            const savedUpdates = {
                release_status: effectiveReleaseStatus,
                sample_collected: sampleCollected === true,
                sample_collected_date: sampleCollected === true ? (sampleCollectedDate || null) : null,
                sample_location: sampleCollected === true ? sampleLocation : '',
                conditional_result: effectiveResult || '',
                guidance_letter_released: guidanceLetterResult || '',
                final_status: finalStatus || '',
                remarks,
                has_test_result: docAttached('TEST RESULT'),
                has_guidance_letter: docAttached('GUIDANCE LETTER'),
                has_sample_collection_form: docAttached('SAMPLE COLLECTION FORM'),
                updated_at: new Date().toISOString(),
            };
            if (effectiveResult === 'FAILED') savedUpdates.failed_remarks = failedRemarks.trim();
            setReportData(prev => prev.map(job => {
                if (job.id !== currentPermit.jobId) return job;
                const patchList = (list) => (list || []).map(p =>
                    p.id === currentPermit.lpcoId ? { ...p, ...savedUpdates } : p);
                return {
                    ...job,
                    submitted_permits: patchList(job.submitted_permits),
                    completed_permits: patchList(job.completed_permits),
                };
            }));

            handleCloseModal();
            fetchReportData(true, searchQuery); // silent refresh — keep the table in place

        } catch (error) {
            toast.error(`Save failed: ${error.message}`);
        } finally {
            setSaving(false);
        }
    };

    const filteredReportData = useMemo(() => {
        if (!searchQuery) return reportData;
        const lowerQuery = searchQuery.toLowerCase();
        return reportData.filter(job =>
            (job.id && job.id.toString().includes(lowerQuery)) ||
            (job.client_name && job.client_name.toLowerCase().includes(lowerQuery)) ||
            (job.bl_number && job.bl_number.toLowerCase().includes(lowerQuery)) ||
            (job.idf_no && job.idf_no.toLowerCase().includes(lowerQuery)) ||
            (job.tansad_no && job.tansad_no.toLowerCase().includes(lowerQuery)) ||
            (job.ucr_no && job.ucr_no.toLowerCase().includes(lowerQuery))
        );
    }, [reportData, searchQuery]);

    // Flatten jobs into permit-level records for qualifying permit types
    const conditionalRecords = useMemo(() => {
        const records = [];
        filteredReportData.forEach(job => {
            (job.submitted_permits || []).forEach(permit => {
                if (!isConditionalPermitType(permit.authority)) return;
                records.push({ job, permit });
            });
            (job.completed_permits || []).forEach(permit => {
                if (!isConditionalPermitType(permit.authority)) return;
                if (records.some(r => r.permit.id === permit.id)) return; // already in submitted list
                records.push({ job, permit });
            });
        });
        return records;
    }, [filteredReportData]);

    // Sample @ Client — collected at the client's premises, collection date
    // (and Sample Collection Form) still pending.
    const sampleClientRecords = useMemo(() =>
        conditionalRecords.filter(r =>
            r.permit.sample_collected === true &&
            (r.permit.sample_location || '').toUpperCase() === 'CLIENT_PREMISES' &&
            !r.permit.sample_collected_date),
        [conditionalRecords]);

    // Awaiting Results — collection date recorded, no test result yet.
    const awaitingRecords = useMemo(() =>
        conditionalRecords.filter(r =>
            r.permit.sample_collected === true && r.permit.sample_collected_date && !r.permit.conditional_result),
        [conditionalRecords]);

    // Guidance Letter Released (Released/Rejected) is a separate outcome from the
    // Test Result — Released moves a failed record to Passed, Rejected to the
    // Guidance Rejected tab.
    const glrOf = (permit) => (permit.guidance_letter_released || '').toUpperCase();
    const finalStatusOf = (permit) => (permit.final_status || '').toUpperCase();
    const passedRecords = useMemo(() =>
        conditionalRecords.filter(r =>
            (r.permit.conditional_result || '').toUpperCase() === 'PASSED' ||
            glrOf(r.permit) === 'RELEASED' ||
            finalStatusOf(r.permit) === 'FINAL_RELEASED'),
        [conditionalRecords]);

    const failedRecords = useMemo(() =>
        conditionalRecords.filter(r =>
            (r.permit.conditional_result || '').toUpperCase() === 'FAILED' &&
            !glrOf(r.permit)),
        [conditionalRecords]);

    // Final Released moves the record to Passed; Final Rejected keeps it here
    // marked as finally rejected — no selection leaves it as Guidance Rejected.
    const guidanceRejectedRecords = useMemo(() =>
        conditionalRecords.filter(r =>
            glrOf(r.permit) === 'REJECTED' && finalStatusOf(r.permit) !== 'FINAL_RELEASED'),
        [conditionalRecords]);

    // Resolve 'job.x' / 'permit.x' style keys for the sortable hook
    const nestedSortValue = (item, key) => key.split('.').reduce((o, k) => (o ? o[k] : undefined), item);
    const defaultSort = { key: 'permit.updated_at', direction: 'descending' };
    const { items: sortedSampleClient, requestSort: sortSampleClient, getSortIndicator: indSampleClient } = useSortableTable(sampleClientRecords, { key: 'permit.approval_date', direction: 'descending' }, nestedSortValue);
    const { items: sortedAwaiting, requestSort: sortAwaiting, getSortIndicator: indAwaiting } = useSortableTable(awaitingRecords, { key: 'permit.approval_date', direction: 'descending' }, nestedSortValue);
    const { items: sortedPassed, requestSort: sortPassed, getSortIndicator: indPassed } = useSortableTable(passedRecords, { key: 'permit.updated_at', direction: 'descending' }, nestedSortValue);
    const { items: sortedFailedRaw, requestSort: sortFailed, getSortIndicator: indFailed } = useSortableTable(failedRecords, defaultSort, nestedSortValue);
    const { items: sortedGuidanceRejected, requestSort: sortGuidanceRejected, getSortIndicator: indGuidanceRejected } = useSortableTable(guidanceRejectedRecords, defaultSort, nestedSortValue);
    // Rows missing a Guidance Letter stay pinned to the top of the Failed tab
    const sortedFailed = useMemo(() =>
        [...sortedFailedRaw].sort((a, b) =>
            (a.permit.has_guidance_letter ? 1 : 0) - (b.permit.has_guidance_letter ? 1 : 0)),
        [sortedFailedRaw]);
    const failedMissingGuidance = failedRecords.some(r => !r.permit.has_guidance_letter);

    const fmtDate = (value) => {
        if (!value) return '-';
        const d = new Date(value);
        if (isNaN(d.getTime())) return '-';
        const dd = String(d.getDate()).padStart(2, '0');
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const yyyy = d.getFullYear();
        return `${dd}/${mm}/${yyyy}`;
    };

    const daysSince = (value) => {
        if (!value) return null;
        const d = new Date(value);
        if (isNaN(d.getTime())) return null;
        const today = new Date();
        d.setHours(0, 0, 0, 0);
        today.setHours(0, 0, 0, 0);
        return Math.floor((today - d) / 86400000);
    };

    // Bold bracketed day count — flashes orange past 5 days, red past 7
    const renderDaysBadge = (value) => {
        const n = daysSince(value);
        if (n === null) return null;
        const cls = n > 7 ? 'days-badge days-danger' : n > 5 ? 'days-badge days-warning' : 'days-badge';
        return <span className={cls}> ({n})</span>;
    };

    const isPendingTab = activeTab === 'awaiting' || activeTab === 'sample-client';

    const renderStatusCell = (record) => {
        const p = record.permit;
        if (activeTab === 'sample-client') {
            return <span style={{ color: '#856404' }}>CLIENT PREMISES</span>;
        }
        if (activeTab === 'awaiting') {
            return <span style={{ color: '#856404' }}>{(p.release_status || '').replace(/_/g, ' ')}</span>;
        }
        const glr = (p.guidance_letter_released || '').toUpperCase();
        const final = (p.final_status || '').toUpperCase();
        const display = final === 'FINAL_RELEASED' ? 'FINAL RELEASED'
            : final === 'FINAL_REJECTED' ? 'FINAL REJECTED'
            : glr === 'RELEASED' ? 'RELEASED'
            : glr === 'REJECTED' ? 'GUIDANCE REJECTED'
            : (p.conditional_result || '').replace(/_/g, ' ');
        return <span style={{ color: activeTab === 'passed' ? '#155724' : '#721c24', fontWeight: 'bold' }}>{display}</span>;
    };

    const renderTable = (records, requestSort, getSortIndicator) => (
        <TableScrollContainer>
            <table className="jobs-table" style={{ overflow: 'visible' }}>
                <thead>
                    <tr>
                        <th onClick={() => requestSort('job.id')}>JOB ID {getSortIndicator('job.id')}</th>
                        <th onClick={() => requestSort('job.idf_no')}>IDF NO. {getSortIndicator('job.idf_no')}</th>
                        <th onClick={() => requestSort('job.tansad_no')}>TANSAD NO. {getSortIndicator('job.tansad_no')}</th>
                        <th onClick={() => requestSort('job.client_name')}>CUSTOMER NAME {getSortIndicator('job.client_name')}</th>
                        <th onClick={() => requestSort('job.bl_number')}>BL NUMBER {getSortIndicator('job.bl_number')}</th>
                        <th onClick={() => requestSort('permit.authority')}>PERMIT TYPE {getSortIndicator('permit.authority')}</th>
                        {isPendingTab ? (
                            <th onClick={() => requestSort('permit.approval_date')}>APPROVED DATE {getSortIndicator('permit.approval_date')}</th>
                        ) : (
                            <th onClick={() => requestSort('permit.updated_at')}>UPDATED DATE {getSortIndicator('permit.updated_at')}</th>
                        )}
                        <th>STATUS</th>
                        {isPendingTab && <th>SAMPLE COLLECTED</th>}
                    </tr>
                </thead>
                <tbody>
                    {loading ? (
                        <tr><td colSpan={isPendingTab ? 9 : 8} style={{ textAlign: 'center' }}>Loading...</td></tr>
                    ) : records.length === 0 ? (
                        <tr><td colSpan={isPendingTab ? 9 : 8} className="no-data">No records found.</td></tr>
                    ) : (
                        records.map((record, index) => {
                            const { job, permit } = record;
                            const flashRow = activeTab === 'failed' && !permit.has_guidance_letter;
                            return (
                                <tr key={`${job.id}-${permit.id}`}
                                    className={flashRow ? 'flash-red' : ''}
                                    title={flashRow ? 'Awaiting Guidance Letter' : undefined}
                                    style={flashRow ? undefined : { backgroundColor: index % 2 === 0 ? '#E3F2FD' : '#ffffff' }}>
                                    <td>
                                        <a href="#" onClick={(e) => { e.preventDefault(); openJobFormModal(job.id); }}>
                                            {job.id}
                                        </a>
                                    </td>
                                    <td>{job.idf_no || '-'}</td>
                                    <td>{job.tansad_no || '-'}</td>
                                    <td><strong>{job.client_short_name || job.client_name}</strong></td>
                                    <td>{job.bl_number}</td>
                                    <td>
                                        <a href="#" onClick={(e) => { e.preventDefault(); handleOpenModal(job.id, permit.authority, job.bl_number, permit); }}>
                                            {permit.authority}
                                        </a>
                                    </td>
                                    {isPendingTab ? (
                                        <td>{permit.approval_date ? <>{fmtDate(permit.approval_date)}{renderDaysBadge(permit.approval_date)}</> : '-'}</td>
                                    ) : (
                                        <td>{fmtDate(permit.updated_at)}</td>
                                    )}
                                    <td>{renderStatusCell(record)}</td>
                                    {isPendingTab && (
                                        <td>{permit.sample_collected
                                            ? (permit.sample_collected_date ? <>{fmtDate(permit.sample_collected_date)}{renderDaysBadge(permit.sample_collected_date)}</> : 'YES')
                                            : 'NO'}</td>
                                    )}
                                </tr>
                            );
                        })
                    )}
                </tbody>
            </table>
        </TableScrollContainer>
    );

    const releaseStatusOptions = getReleaseStatusOptions(currentPermit?.permitType);
    const isPvocModal = (currentPermit?.permitType || '').toUpperCase().includes('PVOC');
    // Collected At shows once a sample is confirmed (and after the PVOC
    // release status is picked); the date input only applies to Port
    // collections or when completing a client-premises record.
    const showCollectedAt = sampleCollected === true && (!isPvocModal || !!releaseStatus);
    const showCollectedDate = showCollectedAt && (sampleLocation === 'PORT' || openedFromClientTab || !!sampleCollectedDate);
    // Saved values open read-only; a delete-permission user's Edit button
    // (isEditingUnlocked) makes them editable again. Unsaved fields stay editable.
    const savedFlags = currentPermit?.saved || {};
    const fieldEditable = (isSaved) => canEdit && (!isSaved || isEditingUnlocked);
    const sampleCollectedEditable = fieldEditable(savedFlags.sampleCollected);
    const releaseStatusEditable = fieldEditable(savedFlags.releaseStatus);
    const sampleLocationEditable = fieldEditable(savedFlags.sampleLocation);
    // Collection date stays editable from the Sample @ Client tab even without
    // edit permission — completing it is that tab's purpose
    const collectedDateEditable = (openedFromClientTab || canEdit) && (!savedFlags.sampleCollectedDate || isEditingUnlocked);
    const conditionalResultEditable = fieldEditable(savedFlags.conditionalResult);
    const failedRemarksEditable = fieldEditable(savedFlags.failedRemarks);
    // Delete-permission users can always change a saved Final status
    const finalStatusEditable = fieldEditable(savedFlags.finalStatus) || canDeletePermits;

    // Documents uploaded against this permit via the Additional Documents
    // flow (tagged with the LPCO id on upload). Workflow documents
    // (TEST RESULT, GUIDANCE LETTER, etc.) are already shown as slots in
    // the main Documents list, so they are excluded here.
    const additionalPermitDocs = jobDocuments.filter(d =>
        d.lpco && d.lpco === currentPermit?.lpcoId &&
        !WORKFLOW_DOC_TYPES.includes((d.document_type || '').toUpperCase())
    );

    // Types mapped to a slot, already attached to this permit (or untagged —
    // a same-type upload would replace them), or queued can't be picked again.
    // Documents tagged to other permits stay selectable — tagging keeps each
    // permit's set separate.
    const extraDocTypeOptions = (currentKey) => {
        const used = new Set([
            ...documentsToUpload.map(d => (d.document_type || '').toUpperCase()),
            ...jobDocuments
                .filter(d => d.lpco == null || d.lpco === currentPermit?.lpcoId)
                .map(d => (d.document_type || '').toUpperCase()),
            ...extraDocs.filter(d => d.key !== currentKey).map(d => (d.document_type || '').toUpperCase()),
        ]);
        return permitDocTypes
            .map(t => ({ id: t.name, text: t.name }))
            .filter(o => !used.has((o.text || '').toUpperCase()));
    };

    if (!hasPermission('PERMIT REPORTS', 'can_view')) {
        return (
            <div style={{ overflowX: 'auto' }}>
                <h2>Permission Denied</h2>
                <p>You do not have permission to view this report.</p>
            </div>
        );
    }

    return (
        <div style={{ overflowX: 'auto' }}>


            <div className="custom-tabs-container" style={{ marginTop: 16, marginBottom: 16 }}>
                <button className={`custom-tab cond-client-tab ${activeTab === 'sample-client' ? 'active' : ''}`} onClick={() => setActiveTab('sample-client')}>
                    Sample @ Client ({sampleClientRecords.length})
                </button>
                <button className={`custom-tab cond-awaiting-tab ${activeTab === 'awaiting' ? 'active' : ''}`} onClick={() => setActiveTab('awaiting')}>
                    Awaiting Results ({awaitingRecords.length})
                </button>
                <button className={`custom-tab cond-failed-tab ${activeTab === 'failed' ? 'active' : ''} ${failedMissingGuidance ? 'deposit-flashing' : ''}`} onClick={() => setActiveTab('failed')}>
                    Failed ({failedRecords.length})
                </button>
                <button className={`custom-tab cond-failed-tab ${activeTab === 'guidance-rejected' ? 'active' : ''}`} onClick={() => setActiveTab('guidance-rejected')}>
                    Guidance Rejected ({guidanceRejectedRecords.length})
                </button>
                <button className={`custom-tab cond-passed-tab ${activeTab === 'passed' ? 'active' : ''}`} onClick={() => setActiveTab('passed')}>
                    Passed ({passedRecords.length})
                </button>
            </div>

            <div className="tab-content">
                {activeTab === 'sample-client' && (
                    <>
                        <div style={{ fontWeight: 'bold', background: '#eef5ff', padding: 8, borderRadius: 6, marginBottom: 8 }}>
                            SAMPLE @ CLIENT — COLLECTION DATE &amp; SAMPLE COLLECTION FORM PENDING
                        </div>
                        {renderTable(sortedSampleClient, sortSampleClient, indSampleClient)}
                    </>
                )}
                {activeTab === 'awaiting' && (
                    <>
                        <div style={{ fontWeight: 'bold', background: '#eef5ff', padding: 8, borderRadius: 6, marginBottom: 8 }}>
                            AWAITING RESULTS — CONDITIONAL / MONITORING
                        </div>
                        {renderTable(sortedAwaiting, sortAwaiting, indAwaiting)}
                    </>
                )}
                {activeTab === 'passed' && (
                    <>
                        <div style={{ fontWeight: 'bold', background: '#eef5ff', padding: 8, borderRadius: 6, marginBottom: 8 }}>
                            PASSED
                        </div>
                        {renderTable(sortedPassed, sortPassed, indPassed)}
                    </>
                )}
                {activeTab === 'failed' && (
                    <>
                        <div style={{ fontWeight: 'bold', background: '#eef5ff', padding: 8, borderRadius: 6, marginBottom: 8 }}>
                            FAILED — ROWS WITHOUT GUIDANCE LETTER ARE HIGHLIGHTED
                        </div>
                        {renderTable(sortedFailed, sortFailed, indFailed)}
                    </>
                )}
                {activeTab === 'guidance-rejected' && (
                    <>
                        <div style={{ fontWeight: 'bold', background: '#eef5ff', padding: 8, borderRadius: 6, marginBottom: 8 }}>
                            GUIDANCE REJECTED
                        </div>
                        {renderTable(sortedGuidanceRejected, sortGuidanceRejected, indGuidanceRejected)}
                    </>
                )}
            </div>

            {isModalOpen && (
                <div className="modal-overlay">
                    <div className="dialog-container" style={{ width: 500 }}>
                        <div className="dialog-header">
                            <h3>Conditional Permit Details for BL: {currentPermit?.blNumber}</h3>
                        </div>
                        <div className="dialog-body" style={{ maxHeight: '75vh', overflowY: 'auto' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                                <p style={{ margin: 0 }}><strong>Permit Type:</strong> {currentPermit?.permitType}</p>
                                <button
                                    type="button"
                                    className="btn-small btn-info-small"
                                    onClick={openHistoryModal}
                                >
                                    History
                                </button>
                            </div>

                            {/* Documents Section — collapsible; auto-expands when a
                                required document is missing */}
                            <div style={{ border: '1px solid #ddd', borderRadius: 4, padding: 12, marginBottom: 16 }}>
                                <div
                                    style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
                                    onClick={() => setDocsCollapsed(c => !c)}
                                >
                                    <label style={{ fontWeight: 'bold', margin: 0, cursor: 'pointer' }}>Documents</label>
                                    <button
                                        type="button"
                                        onClick={(e) => { e.stopPropagation(); setDocsCollapsed(c => !c); }}
                                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#555', fontSize: 13, padding: 0 }}
                                        title={docsExpanded ? 'Collapse' : 'Expand'}
                                    >
                                        <i className={`fa ${docsExpanded ? 'fa-chevron-up' : 'fa-chevron-down'}`}></i>
                                    </button>
                                </div>
                                {docsExpanded && (
                                    <div style={{ marginTop: 8 }}>
                                {visibleDocsToUpload.length === 0 && <p style={{fontSize: '12px', color: '#888'}}>No documents mapped for this permit type.</p>}
                                {visibleDocsToUpload.map((doc) => {
                                    const dt = (doc.document_type || '').toUpperCase();
                                    const isWorkflowDoc = dt === 'TEST RESULT' || dt === 'GUIDANCE LETTER' || dt === 'SAMPLE COLLECTION FORM' || dt === 'INSPECTION BATCH CERTIFICATE';
                                    const showFileInput = isWorkflowDoc && !doc.existing_document && canEdit;

                                    if (doc.file_to_upload) {
                                        return (
                                            <div key={doc.document_type} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12, paddingBottom: 8, borderBottom: '1px solid #eee' }}>
                                                <div style={{ flex: 1, fontWeight: 'bold', fontSize: '13px' }}>{doc.document_type}</div>
                                                <div style={{ flex: 2, display: 'flex', alignItems: 'center', gap: 10 }}>
                                                    <button
                                                        type="button"
                                                        className="btn-small btn-info-small"
                                                        onClick={() => openFilePreview(doc.previewUrl, doc.file_to_upload.name)}
                                                    >
                                                        View
                                                    </button>
                                                    <button type="button" className="btn-small btn-danger-small" onClick={() => cancelFileSelection(doc.document_type)}>
                                                        Cancel
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    }

                                    return (
                                        <div key={doc.document_type} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12, paddingBottom: 8, borderBottom: '1px solid #eee' }}>
                                            <div style={{ flex: 1, fontWeight: 'bold', fontSize: '13px' }}>{doc.document_type}</div>
                                            <div style={{ flex: 2 }}>
                                                {showFileInput ? (
                                                    <input
                                                        type="file"
                                                        className="form-control"
                                                        onChange={(e) => handleFileChange(doc.document_type, e.target.files[0])}
                                                        disabled={saving || !canEdit}
                                                    />
                                                ) : doc.existing_document ? (
                                                    <button
                                                        type="button"
                                                        className="btn-small btn-info-small"
                                                        onClick={() => openFilePreview(doc.existing_document.document_file, doc.existing_document.document_file.split('/').pop())}
                                                    >
                                                        View
                                                    </button>
                                                ) : (
                                                    <span style={{ color: '#888', fontSize: '12px' }}>-</span>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}

                                {/* Additional Documents — attachable from the
                                    Guidance Rejected tab; always listed once any
                                    exist for this permit */}
                                {(openedFromGuidanceRejectedTab || additionalPermitDocs.length > 0) && (
                                    <div style={{ marginTop: 4, borderTop: '1px solid #ddd', paddingTop: 8 }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                                            <label style={{ fontWeight: 'bold', fontSize: '13px', margin: 0 }}>Additional Documents</label>
                                            {openedFromGuidanceRejectedTab && canEdit && (
                                                <button
                                                    type="button"
                                                    className="dialog-btn dialog-btn-primary dialog-btn-sm"
                                                    onClick={addExtraDoc}
                                                    disabled={saving}
                                                >
                                                    Add Document
                                                </button>
                                            )}
                                        </div>
                                        {additionalPermitDocs.length === 0 && extraDocs.length === 0 && (
                                            <p style={{ fontSize: '12px', color: '#888', margin: '4px 0' }}>No additional documents attached.</p>
                                        )}
                                        {additionalPermitDocs.map(doc => (
                                            <div key={doc.id} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 10, paddingBottom: 8, borderBottom: '1px solid #eee' }}>
                                                <div style={{ flex: 1, fontWeight: 'bold', fontSize: '13px' }}>{doc.document_type}</div>
                                                <div style={{ flex: 2, display: 'flex', alignItems: 'center', gap: 10 }}>
                                                    <button
                                                        type="button"
                                                        className="btn-small btn-info-small"
                                                        onClick={() => openFilePreview(doc.document_file, (doc.document_file || '').split('/').pop())}
                                                    >
                                                        View
                                                    </button>
                                                    <div style={{ fontSize: '11px', color: '#666' }}>
                                                        <div>Added: {fmtDate(doc.created_at)}</div>
                                                        {doc.remarks && <div style={{ color: '#555', whiteSpace: 'pre-wrap' }}>{doc.remarks}</div>}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                        {extraDocs.map(doc => (
                                            <div key={doc.key} style={{ border: '1px solid #d4dce3', borderRadius: 4, padding: 8, marginBottom: 8 }}>
                                                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
                                                    <div style={{ flex: 1 }}>
                                                        <Select2Dropdown
                                                            options={extraDocTypeOptions(doc.key)}
                                                            value={doc.document_type}
                                                            onChange={(value) => updateExtraDoc(doc.key, { document_type: value })}
                                                            valueKey="id"
                                                            displayKey="text"
                                                            searchable
                                                        />
                                                    </div>
                                                    <button
                                                        type="button"
                                                        className="btn-small btn-danger-small"
                                                        onClick={() => removeExtraDoc(doc.key)}
                                                        disabled={saving}
                                                    >
                                                        Remove
                                                    </button>
                                                </div>
                                                <input
                                                    type="file"
                                                    className="form-control"
                                                    style={{ marginBottom: 6 }}
                                                    onChange={(e) => setExtraDocFile(doc.key, e.target.files[0])}
                                                    disabled={saving}
                                                />
                                                {doc.file_to_upload && (
                                                    <div style={{ marginBottom: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
                                                        <button
                                                            type="button"
                                                            className="btn-small btn-info-small"
                                                            onClick={() => openFilePreview(doc.previewUrl, doc.file_to_upload.name)}
                                                        >
                                                            View
                                                        </button>
                                                        <span style={{ fontSize: '11px', color: '#555' }}>{doc.file_to_upload.name}</span>
                                                    </div>
                                                )}
                                                <textarea
                                                    className="form-control"
                                                    rows="2"
                                                    style={{ width: '100%' }}
                                                    placeholder="Remarks (required)"
                                                    value={doc.remarks}
                                                    onChange={(e) => updateExtraDoc(doc.key, { remarks: e.target.value })}
                                                    disabled={saving}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                )}
                                    </div>
                                )}
                            </div>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                                <div>
                                    <label style={{ fontWeight: 'bold', display: 'block', marginBottom: 4 }}>Application Number</label>
                                    <div className="form-control" style={{ width: '100%', background: '#f5f5f5', color: '#333' }}>
                                        {authNumber || '-'}
                                    </div>
                                </div>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24, alignItems: 'flex-start' }}>
                                <div>
                                    <label style={{ fontWeight: 'bold', display: 'block', marginBottom: 2 }}>Sample Collected <span style={{ color: 'red' }}>*</span></label>
                                    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                                        {[{ value: true, label: 'Yes' }, { value: false, label: 'No' }].map(option => (
                                            <label key={String(option.value)} style={{ display: 'flex', alignItems: 'center', gap: 3, whiteSpace: 'nowrap', cursor: sampleCollectedEditable ? 'pointer' : 'default' }}>
                                                <input
                                                    type="radio"
                                                    name="sampleCollected"
                                                    checked={sampleCollected === option.value}
                                                    onChange={() => {
                                                        setSampleCollected(option.value);
                                                        if (!option.value) {
                                                            setSampleCollectedDate('');
                                                            setSampleLocation('');
                                                            setConditionalResult('');
                                                        }
                                                    }}
                                                    disabled={!sampleCollectedEditable || saving}
                                                />
                                                {option.label}
                                            </label>
                                        ))}
                                    </div>
                                </div>
                                {isPvocModal && sampleCollected === true && (
                                <div>
                                    <label style={{ fontWeight: 'bold', display: 'block', marginBottom: 2 }}>Release Status <span style={{ color: 'red' }}>*</span></label>
                                    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                                        {releaseStatusOptions.map(option => (
                                            <label key={option.value} style={{ display: 'flex', alignItems: 'center', gap: 3, whiteSpace: 'nowrap', cursor: releaseStatusEditable ? 'pointer' : 'default' }}>
                                                <input
                                                    type="radio"
                                                    name="releaseStatus"
                                                    value={option.value}
                                                    checked={releaseStatus === option.value}
                                                    onChange={(e) => setReleaseStatus(e.target.value)}
                                                    disabled={!releaseStatusEditable || saving}
                                                />
                                                {option.label}
                                            </label>
                                        ))}
                                    </div>
                                </div>
                                )}
                                {showCollectedAt && (
                                    <div>
                                        <label style={{ fontWeight: 'bold', display: 'block', marginBottom: 2 }}>Collected At <span style={{ color: 'red' }}>*</span></label>
                                        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                                            {[{ value: 'PORT', label: 'Port' }, { value: 'CLIENT_PREMISES', label: 'Client Premises' }].map(option => (
                                                <label key={option.value} style={{ display: 'flex', alignItems: 'center', gap: 3, whiteSpace: 'nowrap', cursor: sampleLocationEditable ? 'pointer' : 'default' }}>
                                                    <input
                                                        type="radio"
                                                        name="sampleLocation"
                                                        value={option.value}
                                                        checked={sampleLocation === option.value}
                                                        onChange={(e) => setSampleLocation(e.target.value)}
                                                        disabled={!sampleLocationEditable || saving}
                                                    />
                                                    {option.label}
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                {showCollectedDate && (
                                    <div style={{ maxWidth: 220 }}>
                                        <label style={{ fontWeight: 'bold', display: 'block', marginBottom: 4 }}>Date Collected <span style={{ color: 'red' }}>*</span></label>
                                        <input
                                            type="date"
                                            value={sampleCollectedDate}
                                            onChange={(e) => setSampleCollectedDate(e.target.value)}
                                            max={new Date().toISOString().split('T')[0]}
                                            className="form-control"
                                            style={{ width: '100%' }}
                                            disabled={!collectedDateEditable || saving}
                                        />
                                    </div>
                                )}
                                </div>
                                {sampleCollected === true && !openedFromClientTab && (
                                    <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                                    <div>
                                        <label style={{ fontWeight: 'bold', display: 'block', marginBottom: 2 }}>Test Result</label>
                                        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                                            {CONDITIONAL_RESULT_OPTIONS.filter(o => o.value !== 'GUIDANCE_REJECTED').map(option => (
                                                <label key={option.value} style={{ display: 'flex', alignItems: 'center', gap: 3, whiteSpace: 'nowrap', cursor: conditionalResultEditable ? 'pointer' : 'default' }}>
                                                    <input
                                                        type="radio"
                                                        name="conditionalResult"
                                                        value={option.value}
                                                        checked={conditionalResult === option.value}
                                                        onChange={(e) => {
                                                            setConditionalResult(e.target.value);
                                                            if (e.target.value !== 'FAILED') setFailedRemarks('');
                                                        }}
                                                        disabled={!conditionalResultEditable || saving}
                                                    />
                                                    {option.label}
                                                </label>
                                            ))}
                                        </div>
                                        <p style={{ fontSize: '12px', color: '#888', margin: '6px 0 0' }}>
                                            Selecting a result requires a Test Result document{openedFromFailedTab ? '. The Guidance Letter is attached here for failed samples.' : ''}
                                        </p>
                                    </div>
                                    {conditionalResult === 'FAILED' && (
                                        <div style={{ flex: '1 1 260px', minWidth: 220 }}>
                                            <label style={{ fontWeight: 'bold', display: 'block', marginBottom: 4 }}>Failed Remarks <span style={{ color: 'red' }}>*</span></label>
                                            <textarea
                                                value={failedRemarks}
                                                onChange={(e) => setFailedRemarks(e.target.value)}
                                                className="form-control"
                                                rows="3"
                                                style={{ width: '100%' }}
                                                placeholder="Enter reason for the failed result..."
                                                readOnly={!failedRemarksEditable}
                                            />
                                        </div>
                                    )}
                                    </div>
                                )}
                                {openedFromFailedTab && conditionalResult === 'FAILED' && (
                                    <div>
                                        <label style={{ fontWeight: 'bold', display: 'block', marginBottom: 2 }}>Guidance Letter Released</label>
                                        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                                            {[{ value: 'RELEASED', label: 'Released' }, { value: 'REJECTED', label: 'Rejected' }].map(option => (
                                                <label key={option.value} style={{ display: 'flex', alignItems: 'center', gap: 3, whiteSpace: 'nowrap', cursor: canEdit && (!savedFlags.guidanceLetterReleased || isEditingUnlocked) ? 'pointer' : 'default' }}>
                                                    <input
                                                        type="radio"
                                                        name="guidanceLetterResult"
                                                        value={option.value}
                                                        checked={guidanceLetterResult === option.value}
                                                        onChange={(e) => setGuidanceLetterResult(e.target.value)}
                                                        disabled={!canEdit || (savedFlags.guidanceLetterReleased && !isEditingUnlocked) || saving}
                                                    />
                                                    {option.label}
                                                </label>
                                            ))}
                                        </div>
                                        <p style={{ fontSize: '12px', color: '#888', margin: '6px 0 0' }}>
                                            Released moves the permit to Passed; Rejected moves it to Guidance Rejected.
                                        </p>
                                    </div>
                                )}
                                <div>
                                    <label style={{ fontWeight: 'bold', display: 'block', marginBottom: 4 }}>Remarks {((isPvocModal ? releaseStatus : 'CONDITIONAL') || sampleCollected === true || conditionalResult) && <span style={{ color: 'red' }}>*</span>}</label>
                                    <textarea
                                        value={remarks}
                                        onChange={(e) => setRemarks(e.target.value)}
                                        className="form-control"
                                        rows="3"
                                        style={{ width: '100%' }}
                                        readOnly={!canEdit || (savedFlags.remarks && !isEditingUnlocked)}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="dialog-footer" style={{ position: 'relative' }}>
                            {canDeletePermits && !isEditingUnlocked && (
                                <button
                                    type="button"
                                    className="dialog-btn dialog-btn-secondary"
                                    style={{ marginRight: 'auto' }}
                                    onClick={() => setIsEditingUnlocked(true)}
                                    disabled={saving}
                                >
                                    Edit
                                </button>
                            )}
                            {(openedFromGuidanceRejectedTab || (savedFlags.finalStatus && canDeletePermits)) && (
                                <div style={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2 }}>
                                    {[
                                        { value: 'FINAL_REJECTED', label: 'Final Rejected', color: '#c62828' },
                                        { value: 'FINAL_RELEASED', label: 'Final Released', color: '#14833b' },
                                    ].map(option => (
                                        <label key={option.value} style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 'bold', color: option.color, whiteSpace: 'nowrap', cursor: finalStatusEditable ? 'pointer' : 'default', margin: 0 }}>
                                            <input
                                                type="checkbox"
                                                checked={finalStatus === option.value}
                                                onChange={(e) => setFinalStatus(e.target.checked ? option.value : '')}
                                                disabled={!finalStatusEditable || saving}
                                            />
                                            {option.label}
                                        </label>
                                    ))}
                                </div>
                            )}
                            <button type="button" className="dialog-btn dialog-btn-secondary" onClick={handleCloseModal} disabled={saving}>Cancel</button>
                            {(canEdit || ((openedFromGuidanceRejectedTab || savedFlags.finalStatus) && canDeletePermits)) && (
                                <button type="button" className="dialog-btn dialog-btn-primary" onClick={handleSavePermit} disabled={saving}>
                                    {saving ? 'Saving...' : 'Save'}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Permit Change History Modal */}
            {historyModal.open && (
                <div className="modal-overlay" style={{ zIndex: 1100 }}>
                    <div className="dialog-container" style={{ width: 640, maxWidth: '95%' }}>
                        <div className="dialog-header">
                            <h3>Change History — {currentPermit?.permitType} (BL: {currentPermit?.blNumber})</h3>
                        </div>
                        <div className="dialog-body" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                            {historyModal.loading ? (
                                <p style={{ color: '#888' }}>Loading history...</p>
                            ) : historyModal.rows.length === 0 ? (
                                <p style={{ color: '#888' }}>No changes recorded yet.</p>
                            ) : (
                                <table className="jobs-table">
                                    <thead>
                                        <tr>
                                            <th>Date</th>
                                            <th>Updated By</th>
                                            <th>Change Made</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {historyModal.rows.map((row, idx) => (
                                            <tr key={idx} style={{ backgroundColor: idx % 2 === 0 ? '#E3F2FD' : '#ffffff' }}>
                                                <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(row.date)}</td>
                                                <td>{(row.user || '-').toUpperCase()}</td>
                                                <td>{row.changes.map((c, i) => <div key={i}>{c.toUpperCase()}</div>)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                        <div className="dialog-footer">
                            <button
                                type="button"
                                className="dialog-btn dialog-btn-secondary"
                                onClick={() => setHistoryModal({ open: false, loading: false, rows: [] })}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Job Form Modal */}
            {jobFormModal.isOpen && (
                <div className="modal-overlay">
                    <div className="dialog-container" style={{
                        width: '95%',
                        maxWidth: '1400px',
                        height: '90vh',
                        display: 'flex',
                        flexDirection: 'column',
                        padding: 0,
                        overflow: 'hidden'
                    }}>
                        <div className="dialog-header" style={{ padding: '10px 20px', borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
                            <h3 style={{ margin: 0 }}>Job Details</h3>
                            <button onClick={closeJobFormModal} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#666' }}>&times;</button>
                        </div>
                        <div className="dialog-body" style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>
                            <JobForm job={jobFormModal.job} onSave={closeJobFormModal} />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ConditionalPermits;
